import { Suspense, useCallback } from "react";
import type { PropsWithChildren } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { ProductKeys } from "@atlassiansox/asc-stargate";

import { CrossFlowProvider } from "@/components/cross-flow/services/index";
import { CrossFlowTouchpointProvider } from "@/components/cross-flow/touchpoint/context";
import ErrorBoundary from "@/components/error-boundary";
import type { Template } from "@/content/templates/template.types";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles";

import { JSM_TEMPLATES_CONTENT } from "./content/templates-grid-content";
import { Resources } from "./sections/resources";
import { SharedHeaderFooterContainer } from "./sections/shared-header-footer-container";
import { TemplatesGrid } from "./sections/templates-grid";
export type JiraTemplateCategoryPageTemplateProps = PropsWithChildren<{
    templates?: Template[];
}>;

const PageContent = ({ templates }: { templates?: Template[] }) => {
    return (
        <SharedHeaderFooterContainer>
            <TypographyStyles>
                <Suspense>
                    <TemplatesGrid
                        templates={templates ?? JSM_TEMPLATES_CONTENT}
                    />
                </Suspense>
                <Resources />
            </TypographyStyles>
        </SharedHeaderFooterContainer>
    );
};

export const JiraTemplateCategoryPageTemplate = ({
    templates
}: JiraTemplateCategoryPageTemplateProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const fireSLOErrorEvent = useCallback(
        (error: unknown) => {
            createAnalyticsEvent({
                product: "marketing",
                eventType: "operational",
                actionSubject: "wacJsmTemplateScreenFailed",
                action: "failed",
                attributes: {
                    errorMessage: (error as Error)?.message || error
                }
            }).fire();
        },
        [createAnalyticsEvent]
    );

    return (
        <ErrorBoundary
            fallback={<PageContent templates={templates} />}
            onError={(e) => fireSLOErrorEvent(e)}
        >
            <CrossFlowProvider>
                <CrossFlowTouchpointProvider
                    productKeys={[ProductKeys.JIRA_SERVICE_DESK]}
                    screenName="wacJsmTemplateScreen"
                >
                    <PageContent templates={templates} />
                </CrossFlowTouchpointProvider>
            </CrossFlowProvider>
        </ErrorBoundary>
    );
};
